import type { JSX, MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { navigate } from '../lib/router';
import { IconArrowLeft, IconCheck, IconSparkle } from './icons';
import '../styles/novidades.css';

/**
 * ECO4.9-S (26/09/2026) — NOVIDADES. O dono disse que "não teve confirmação clara" do que o brainstorm de 25/09
 * entregou. Esta tela responde em 10 s: cada entrega é um cartão com a TELA REAL (captura da produção ou da
 * validação), um título de 3–5 palavras, uma frase de valor e "Abrir" — sem texto técnico. "novo" some depois que
 * ele abre a entrega (guardado só neste aparelho).
 */

interface Novidade {
  id: string;
  title: string;
  value: string;
  href: string;
  image: string;
  date: string;
  /** onde fica, quando não é uma tela própria */
  where?: string;
}

/** Ordem: mais recente primeiro. */
export const NOVIDADES: Novidade[] = [
  { id: 'voz', title: 'Voz como no Gravador', value: 'Tempo grande, onda ao vivo, Cancelar e Parar e transcrever — no app, pode bloquear a tela.', href: '/', image: 'voz', date: '27/09' },
  { id: 'deck', title: 'Quem responde por quê', value: 'Cada projeto mostra os assuntos dele; a sessão aberta fora vem para cá num toque.', href: '/deck', image: 'deck', date: '27/09', where: 'no cartão de cada projeto' },
  { id: 'temas', title: 'Claude ou Codex num toque', value: 'Cada Tema mostra em qual provedor está e troca sem perder o contexto.', href: '/deck', image: 'temas', date: '27/09', where: 'botão Temas do projeto' },
  { id: 'app', title: 'App Android sem erro', value: 'As telas do Android, passo a passo, com o botão certo marcado — e o app avisa sozinho se algo falhar.', href: '/app', image: 'app', date: '27/09' },
  { id: 'sessoes', title: 'Mapa das sessões', value: 'Quem está trabalhando, o que está fazendo agora e quem conversa com quem.', href: '/sessoes', image: 'sessoes', date: '27/09' },
  { id: 'conversa', title: 'Terminais conversando', value: 'Uma sessão pede à outra e você vê enviada, entregue, lida e respondida, com o horário.', href: '/sessoes', image: 'conversa', date: '27/09', where: 'aba Conversas' },
  { id: 'cerebro', title: 'Seu segundo cérebro', value: 'Suas decisões e ideias ligadas num mapa; anote por voz e o Assistente guarda o que você decidir.', href: '/cerebro', image: 'cerebro', date: '26/09' },
  { id: 'janela', title: 'Terminal em janela', value: 'Abra qualquer terminal numa janela do Windows e continue igual no term-deck.', href: '/deck', image: 'janela', date: '26/09', where: 'botão de janela no terminal' },
];

const SEEN_KEY = 'td.novidades.vistas';

function readSeen(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[]); } catch { return new Set(); }
}

function markSeen(id: string): void {
  try {
    const seen = readSeen();
    seen.add(id);
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen]));
  } catch { /* armazenamento bloqueado: o selo só não some */ }
}

/** Quantas novidades ainda não foram abertas neste aparelho (para o selo no menu). */
export function unseenNovidades(): number {
  const seen = readSeen();
  return NOVIDADES.filter((n) => !seen.has(n.id)).length;
}

export function NovidadesPage(): JSX.Element {
  const [seen, setSeen] = useState<Set<string>>(() => readSeen());
  useEffect(() => { document.title = 'Novidades · term-deck'; }, []);
  const fresh = useMemo(() => NOVIDADES.filter((n) => !seen.has(n.id)).length, [seen]);

  const open = (event: MouseEvent, n: Novidade) => {
    event.preventDefault();
    markSeen(n.id);
    setSeen(readSeen());
    navigate(n.href);
  };

  return (
    <main className="nov-page" data-testid="novidades-page">
      <header className="nov-hero">
        <span className="nov-mark" aria-hidden="true"><IconSparkle /></span>
        <div>
          <h1>Novidades</h1>
          <p>O que ficou pronto do brainstorm de 25/09 — {NOVIDADES.length} entregas{fresh ? `, ${fresh} que você ainda não abriu` : ', todas já vistas'}.</p>
        </div>
      </header>
      <ul className="nov-grid">
        {NOVIDADES.map((n) => {
          const isNew = !seen.has(n.id);
          return (
            <li key={n.id}>
              <a className="nov-card" href={n.href} onClick={(e) => open(e, n)} data-testid="novidade">
                <span className="nov-shot">
                  <img src={`/novidades/${n.image}.webp`} alt={`tela: ${n.title}`} width={640} height={360} loading="lazy" />
                  {isNew ? <span className="nov-new">novo</span> : <span className="nov-seen"><IconCheck />visto</span>}
                </span>
                <span className="nov-body">
                  <span className="nov-date">{n.date}{n.where ? ` · ${n.where}` : ''}</span>
                  <b>{n.title}</b>
                  <span className="nov-value">{n.value}</span>
                  <span className="nov-open">Abrir <IconArrowLeft /></span>
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </main>
  );
}
